/**
 * InterstitialAd Component — Fullscreen Ad Overlay
 * ==================================================
 * Shows a fullscreen ad overlay with a countdown timer before
 * allowing the user to proceed with their action.
 *
 * Usage:
 *   import { showInterstitialAd } from './InterstitialAd/index.js';
 *   showInterstitialAd(() => { doSomething(); });
 *
 * The overlay shows a placeholder ad, a countdown (default 3s),
 * then enables a CONTINUE button. User can also close via the X button.
 */
import { t } from '../../services/i18n.js';

let interstitialCount = 0;  // Track how many times shown this session
const SHOW_EVERY_N = 3;     // Show ad every 3rd action (AdSense best practice)

let lastActiveTime = Date.now();
let isAdActive = false;

// AdSense "Return from Idle" strategy
// If user leaves tab for >15 seconds and comes back, serve an ad immediately
window.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    lastActiveTime = Date.now();
  } else if (document.visibilityState === 'visible') {
    const idleSeconds = (Date.now() - lastActiveTime) / 1000;
    if (idleSeconds > 65 && !isAdActive) {
      showInterstitialAd(() => { console.log("Return from Idle Ad Finished") }, { forceShow: true });
    }
  }
});

/**
 * Show an interstitial ad overlay before executing a callback.
 * @param {Function} callback - The action to run after the ad.
 * @param {Object} [options]
 * @param {number} [options.duration=3] - Countdown seconds before skip is available.
 * @param {string} [options.adSize='336x280'] - Display ad slot dimensions.
 * @param {boolean} [options.forceShow=false] - Bypass the Nth count logic.
 * @param {boolean} [options.bypassAd=false] - Skip the ad immediately (e.g., for exports).
 */
export function showInterstitialAd(callback, options = {}) {
  const { duration = 3, adSize = '336x280', forceShow = false, bypassAd = false } = options;

  if (bypassAd) {
    callback();
    return;
  }

  interstitialCount++;

  // Only show ad every Nth action (unless forced)
  if (!forceShow && interstitialCount % SHOW_EVERY_N !== 0) {
    callback();
    return;
  }

  isAdActive = true;

  // Create overlay
  const overlay = document.createElement('div');
  overlay.className = 'cm-interstitial';
  overlay.innerHTML = `
    <div class="cm-interstitial__backdrop"></div>
    <div class="cm-interstitial__container">
      <div class="cm-interstitial__header">
        <span class="cm-interstitial__label">SPONSORED</span>
        <button class="cm-interstitial__skip" disabled>
          <span class="cm-interstitial__timer">${duration}</span>
        </button>
      </div>
      <div class="cm-interstitial__ad">
        <div class="cm-interstitial__ad-inner">
          <div class="cm-interstitial__ad-placeholder">
            <span class="cm-interstitial__ad-size">${adSize}</span>
            <span class="cm-interstitial__ad-text">ADVERTISEMENT</span>
          </div>
        </div>
      </div>
      <div class="cm-interstitial__footer">
        <button class="cm-interstitial__continue" disabled>CONTINUE</button>
      </div>
      <div class="cm-interstitial__notice">${t('AD_NOTICE', 'Ads help us keep this tool free. Thank you for your patience!')}</div>
    </div>
  `;

  document.body.appendChild(overlay);

  // Force reflow then animate in
  requestAnimationFrame(() => {
    overlay.classList.add('cm-interstitial--visible');
  });

  const skipBtn = overlay.querySelector('.cm-interstitial__skip');
  const continueBtn = overlay.querySelector('.cm-interstitial__continue');
  const timerSpan = overlay.querySelector('.cm-interstitial__timer');

  let remaining = duration;

  const closeOverlay = () => {
    overlay.classList.remove('cm-interstitial--visible');
    isAdActive = false;
    setTimeout(() => overlay.remove(), 200);
    callback();
  };

  // Countdown
  const interval = setInterval(() => {
    remaining--;
    if (remaining <= 0) {
      clearInterval(interval);
      skipBtn.disabled = false;
      skipBtn.innerHTML = '\u2715';
      skipBtn.classList.add('cm-interstitial__skip--active');
      continueBtn.disabled = false;
      continueBtn.classList.add('cm-interstitial__continue--active');
    } else {
      timerSpan.textContent = remaining;
    }
  }, 1000);

  skipBtn.addEventListener('click', () => {
    if (!skipBtn.disabled) {
      clearInterval(interval);
      closeOverlay();
    }
  });

  continueBtn.addEventListener('click', () => {
    if (!continueBtn.disabled) {
      clearInterval(interval);
      closeOverlay();
    }
  });

  // Also close on backdrop click after countdown
  overlay.querySelector('.cm-interstitial__backdrop').addEventListener('click', () => {
    if (!skipBtn.disabled) {
      clearInterval(interval);
      closeOverlay();
    }
  });

  // Prevent keyboard events from bubbling through to the keyboard tester
  overlay.addEventListener('keydown', (e) => e.stopPropagation());
  overlay.addEventListener('keyup', (e) => e.stopPropagation());
}
